import { useState, useEffect } from 'react';
import { 
  FileText, 
  Send, 
  X, 
  Plus, 
  Trash2, 
  Mail,
  AlertCircle,
  CheckCircle,
  Loader2,
  Info,
  Crown,
  User,
  Phone,
  Stethoscope,
  ChevronRight,
  Save
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RichiestaRicettaModal = ({ 
  isOpen, 
  onClose, 
  medicoSettings: initialMedicoSettings, 
  pazienteNome,
  pazienteEta,
  famigliaNome,
  userEmail,
  userPhone,
  famigliaId,
  medicines = [],
  onMedicoConfigured
}) => {
  const [medicoSettings, setMedicoSettings] = useState(initialMedicoSettings || {});
  const [selectedMedicines, setSelectedMedicines] = useState([]);
  const [customMedicine, setCustomMedicine] = useState('');
  const [note, setNote] = useState('');
  const [replyToEmail, setReplyToEmail] = useState(userEmail || '');
  const [replyToPhone, setReplyToPhone] = useState(userPhone || '');
  const [useCustomContact, setUseCustomContact] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showMedicoConfig, setShowMedicoConfig] = useState(!initialMedicoSettings?.email_medico);
  const [savingMedico, setSavingMedico] = useState(false);
  
  // Form per configurazione medico
  const [medicoForm, setMedicoForm] = useState({
    nome_medico: initialMedicoSettings?.nome_medico || '',
    email_medico: initialMedicoSettings?.email_medico || '',
    telefono_medico: initialMedicoSettings?.telefono_medico || '',
    indirizzo_studio: initialMedicoSettings?.indirizzo_studio || ''
  });
  
  // Track if initial settings have been loaded to prevent re-initialization
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    // Only initialize once when initialMedicoSettings first arrives
    if (initialMedicoSettings && !initialLoaded) {
      setMedicoSettings(initialMedicoSettings);
      setMedicoForm({
        nome_medico: initialMedicoSettings.nome_medico || '',
        email_medico: initialMedicoSettings.email_medico || '',
        telefono_medico: initialMedicoSettings.telefono_medico || '',
        indirizzo_studio: initialMedicoSettings.indirizzo_studio || ''
      });
      setShowMedicoConfig(!initialMedicoSettings.email_medico);
      setInitialLoaded(true);
    }
  }, [initialMedicoSettings, initialLoaded]);

  if (!isOpen) return null;

  const toggleMedicine = (medicine) => {
    setSelectedMedicines(prev => 
      prev.includes(medicine) 
        ? prev.filter(m => m !== medicine)
        : [...prev, medicine]
    );
  };

  const addCustomMedicine = () => {
    if (customMedicine.trim() && !selectedMedicines.includes(customMedicine.trim())) {
      setSelectedMedicines(prev => [...prev, customMedicine.trim()]);
      setCustomMedicine('');
    }
  };

  const removeMedicine = (medicine) => {
    setSelectedMedicines(prev => prev.filter(m => m !== medicine));
  };

  // Salva configurazione medico
  const saveMedicoConfig = async () => {
    if (!medicoForm.nome_medico || !medicoForm.email_medico) {
      toast.error('Inserisci nome e email del medico');
      return;
    }

    setSavingMedico(true);
    try {
      const response = await fetch(`${API_URL}/api/family/${famigliaId}/medico`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(medicoForm)
      });

      if (response.ok) {
        setMedicoSettings(medicoForm);
        setShowMedicoConfig(false);
        toast.success('Dati del medico salvati!');
        if (onMedicoConfigured) onMedicoConfigured(medicoForm);
      } else {
        throw new Error('Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio: ' + error.message);
    } finally {
      setSavingMedico(false);
    }
  };

  const handleSend = async () => {
    if (selectedMedicines.length === 0) {
      toast.error('Seleziona almeno un farmaco');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/email/prescription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          medico_email: medicoSettings.email_medico,
          medico_nome: medicoSettings.nome_medico,
          paziente_nome: pazienteNome,
          paziente_eta: pazienteEta,
          farmaci: selectedMedicines,
          note: note || null,
          famiglia_nome: famigliaNome,
          reply_to: replyToEmail,
          famiglia_id: famigliaId,
          telefono_familiare: replyToPhone
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSent(true);
        toast.success('Richiesta ricetta inviata al medico!');
        setTimeout(() => {
          onClose();
          setSent(false);
          setSelectedMedicines([]);
          setNote('');
        }, 2000);
      } else {
        throw new Error(data.detail || 'Errore invio');
      }
    } catch (error) {
      toast.error('Errore nell\'invio: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  // Medico form handlers - using functional updates to avoid stale closures
  const handleNomeMedicoChange = (e) => {
    const value = e.target.value;
    setMedicoForm(prev => ({...prev, nome_medico: value}));
  };
  
  const handleEmailMedicoChange = (e) => {
    const value = e.target.value;
    setMedicoForm(prev => ({...prev, email_medico: value}));
  };
  
  const handleTelefonoMedicoChange = (e) => {
    const value = e.target.value;
    setMedicoForm(prev => ({...prev, telefono_medico: value}));
  };
  
  const handleIndirizzoMedicoChange = (e) => {
    const value = e.target.value;
    setMedicoForm(prev => ({...prev, indirizzo_studio: value}));
  };

  // Email preview content rendered inline to avoid nested component issues

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#4A7C59] to-[#6B9B7A] text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Richiedi Ricetta</h2>
              <p className="text-sm text-white/80">
                {showMedicoConfig 
                  ? 'Configura il medico di base' 
                  : `Invia richiesta al ${medicoSettings.nome_medico}`}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {sent ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-[#2C3E50] mb-2">Richiesta Inviata!</h3>
              <p className="text-[#64748B]">
                Il {medicoSettings.nome_medico} riceverà la richiesta e potrà contattarti a {replyToEmail}
                {replyToPhone && ` o al ${replyToPhone}`}
              </p>
            </div>
          ) : showMedicoConfig ? (
            <div className="space-y-5">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Stethoscope className="w-8 h-8 text-[#4A7C59]" />
                </div>
                <h3 className="text-xl font-bold text-[#2C3E50]">Configura il Medico di Base</h3>
                <p className="text-sm text-[#64748B] mt-1">
                  Inserisci i dati del medico per poter richiedere le ricette
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Nome del Medico *
                </label>
                <input
                  type="text"
                  value={medicoForm.nome_medico}
                  onChange={handleNomeMedicoChange}
                  placeholder="es. Dr. Mario Rossi"
                  className="w-full h-12 px-4 border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] outline-none"
                  data-testid="input-nome-medico"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Email del Medico *
                </label>
                <input
                  type="email"
                  value={medicoForm.email_medico}
                  onChange={handleEmailMedicoChange}
                  placeholder="es. dott.rossi@email.com"
                  className="w-full h-12 px-4 border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] outline-none"
                  data-testid="input-email-medico"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Telefono Studio (opzionale)
                </label>
                <input
                  type="tel"
                  value={medicoForm.telefono_medico}
                  onChange={handleTelefonoMedicoChange}
                  placeholder="es. 02 1234567"
                  className="w-full h-12 px-4 border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] outline-none"
                  data-testid="input-telefono-medico"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Indirizzo Studio (opzionale)
                </label>
                <input
                  type="text"
                  value={medicoForm.indirizzo_studio}
                  onChange={handleIndirizzoMedicoChange}
                  placeholder="es. Via Roma 123, Milano"
                  className="w-full h-12 px-4 border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] outline-none"
                  data-testid="input-indirizzo-medico"
                />
              </div>

              <div className="bg-[#F0F9F1] rounded-xl p-4 border border-[#C8E6C9]">
                <p className="text-sm text-[#4A7C59]">
                  <strong>Nota:</strong> Questi dati verranno salvati e potrai modificarli in seguito nelle Impostazioni.
                </p>
              </div>
            </div>
          ) : showPreview ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-[#2C3E50]">Anteprima Email</h3>
                <Button 
                  onClick={() => setShowPreview(false)}
                  variant="outline"
                  size="sm"
                >
                  ← Modifica
                </Button>
              </div>
              <div className="bg-white rounded-xl border-2 border-[#4A7C59] overflow-hidden">
                <div className="bg-gradient-to-r from-[#4A7C59] to-[#6B9B7A] text-white p-4 text-center">
                  <h3 className="font-bold text-lg">Richiesta Ricetta Medica</h3>
                  <p className="text-sm text-white/80">via CuraCheck</p>
                </div>
                <div className="p-5 space-y-4">
                  <p className="text-[#2C3E50]">
                    Gentile <strong>{medicoSettings.nome_medico}</strong>,
                  </p>
                  <p className="text-[#2C3E50]">
                    Le scriviamo per conto della famiglia <strong>{famigliaNome}</strong> per richiedere una ricetta per il/la paziente:
                  </p>
                  <div className="bg-[#E8F5E9] rounded-lg p-4 text-center">
                    <h4 className="text-xl font-bold text-[#2C3E50]">
                      {pazienteNome} {pazienteEta ? `(${pazienteEta} anni)` : ''}
                    </h4>
                  </div>
                  <div className="bg-[#F0F9F1] rounded-lg p-4">
                    <h4 className="font-bold text-[#4A7C59] mb-2">Farmaci richiesti:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {selectedMedicines.map((med, i) => (
                        <li key={i} className="text-[#2C3E50]">{med}</li>
                      ))}
                    </ul>
                  </div>
                  {note && (
                    <div className="bg-[#FEF3C7] rounded-lg p-4">
                      <h4 className="font-semibold text-[#92400E] mb-1">Note:</h4>
                      <p className="text-[#B45309]">{note}</p>
                    </div>
                  )}
                  
                  <div className="bg-[#EBF5FB] rounded-lg p-4">
                    <h4 className="font-semibold text-[#2471A3] mb-2">Contatti Familiare:</h4>
                    <p className="text-sm text-[#2471A3]">
                      <strong>Email:</strong> {replyToEmail}<br/>
                      {replyToPhone && <><strong>Telefono:</strong> {replyToPhone}</>}
                    </p>
                  </div>
                  
                  <p className="text-[#2C3E50]">
                    Cordiali saluti,<br/>
                    <strong>Il team CuraCheck</strong>
                  </p>
                  <div className="border-t pt-3 text-center text-xs text-[#64748B]">
                    <p>Per rispondere, il medico puo usare Rispondi o chiamare direttamente</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Medico selezionato */}
              <div className="bg-[#F0F9F1] rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#4A7C59] rounded-full flex items-center justify-center">
                    <Stethoscope className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C3E50]">{medicoSettings.nome_medico}</p>
                    <p className="text-sm text-[#64748B]">{medicoSettings.email_medico}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowMedicoConfig(true)}
                  className="text-sm text-[#4A7C59] underline hover:text-[#3A6345]"
                >
                  Modifica
                </button>
              </div>

              {/* Paziente Info */}
              <div className="bg-[#F4F7F5] rounded-xl p-4">
                <p className="text-sm text-[#64748B]">Paziente:</p>
                <p className="text-lg font-bold text-[#2C3E50]">
                  {pazienteNome} {pazienteEta ? `(${pazienteEta} anni)` : ''}
                </p>
              </div>

              {/* Farmaci Configurati */}
              {medicines.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                    Farmaci abituali (clicca per selezionare):
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {medicines.map((med, i) => (
                      <button
                        key={i}
                        onClick={() => toggleMedicine(med.nome || med)}
                        className={`px-4 py-2 rounded-full border-2 font-medium transition-all ${
                          selectedMedicines.includes(med.nome || med)
                            ? 'bg-[#4A7C59] border-[#4A7C59] text-white'
                            : 'bg-white border-[#E5E7EB] text-[#2C3E50] hover:border-[#4A7C59]'
                        }`}
                      >
                        {med.nome || med}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Aggiungi Farmaco Custom */}
              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Aggiungi altro farmaco:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customMedicine}
                    onChange={(e) => setCustomMedicine(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomMedicine()}
                    placeholder="Nome farmaco..."
                    className="flex-1 h-12 px-4 border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] outline-none"
                  />
                  <Button 
                    onClick={addCustomMedicine}
                    disabled={!customMedicine.trim()}
                    className="h-12 px-4 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl"
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Farmaci Selezionati */}
              {selectedMedicines.length > 0 && (
                <div className="bg-[#E8F5E9] rounded-xl p-4">
                  <p className="text-sm font-semibold text-[#4A7C59] mb-2">
                    Farmaci selezionati ({selectedMedicines.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedMedicines.map((med, i) => (
                      <span 
                        key={i}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full text-sm font-medium text-[#2C3E50] border border-[#C8E6C9]"
                      >
                        {med}
                        <button 
                          onClick={() => removeMedicine(med)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Note */}
              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Note per il medico (opzionale):
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Es: Urgente, il farmaco sta finendo... oppure informazioni aggiuntive..."
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] outline-none resize-none"
                />
              </div>

              {/* Contatti per la risposta */}
              <div className="bg-[#FEF3C7] rounded-xl p-4 border border-[#F59E0B]/30">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#92400E] mb-3">
                      Il medico risponderà a questi contatti:
                    </p>
                    
                    {useCustomContact ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-[#B45309] font-medium">Email</label>
                          <input
                            type="email"
                            value={replyToEmail}
                            onChange={(e) => setReplyToEmail(e.target.value)}
                            placeholder="altra@email.com"
                            className="w-full h-10 px-3 border-2 border-[#F59E0B]/30 rounded-lg focus:border-[#F59E0B] outline-none bg-white text-sm mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#B45309] font-medium">Telefono</label>
                          <input
                            type="tel"
                            value={replyToPhone}
                            onChange={(e) => setReplyToPhone(e.target.value)}
                            placeholder="+39 333 1234567"
                            className="w-full h-10 px-3 border-2 border-[#F59E0B]/30 rounded-lg focus:border-[#F59E0B] outline-none bg-white text-sm mt-1"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-sm text-[#B45309]">
                          <Mail className="w-4 h-4 inline mr-2" />
                          <strong>{userEmail}</strong>
                        </p>
                        {userPhone && (
                          <p className="text-sm text-[#B45309]">
                            <Phone className="w-4 h-4 inline mr-2" />
                            <strong>{userPhone}</strong>
                          </p>
                        )}
                      </div>
                    )}
                    
                    <button
                      onClick={() => {
                        setUseCustomContact(!useCustomContact);
                        if (useCustomContact) {
                          setReplyToEmail(userEmail);
                          setReplyToPhone(userPhone);
                        }
                      }}
                      className="text-xs text-[#F59E0B] underline mt-3 hover:text-[#D97706]"
                    >
                      {useCustomContact ? 'Usa contatti account' : 'Modifica contatti'}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!sent && (
          <div className="border-t border-[#E5E7EB] p-5 bg-[#F9FAFB]">
            {showMedicoConfig ? (
              <div className="flex gap-3">
                {initialMedicoSettings?.email_medico && (
                  <Button
                    onClick={() => setShowMedicoConfig(false)}
                    variant="outline"
                    className="flex-1 h-12 border-[#E5E7EB]"
                  >
                    Annulla
                  </Button>
                )}
                <Button
                  onClick={saveMedicoConfig}
                  disabled={savingMedico || !medicoForm.nome_medico || !medicoForm.email_medico}
                  className={`h-12 bg-[#4A7C59] hover:bg-[#3A6345] text-white ${initialMedicoSettings?.email_medico ? 'flex-1' : 'w-full'}`}
                >
                  {savingMedico ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Salva e Continua
                    </>
                  )}
                </Button>
              </div>
            ) : showPreview ? (
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowPreview(false)}
                  variant="outline"
                  className="flex-1 h-12 border-[#E5E7EB]"
                >
                  ← Modifica
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={sending}
                  className="flex-1 h-12 bg-[#4A7C59] hover:bg-[#3A6345] text-white"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Invia al Medico
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 h-12 border-[#E5E7EB]"
                >
                  Annulla
                </Button>
                <Button
                  onClick={() => setShowPreview(true)}
                  disabled={selectedMedicines.length === 0}
                  className="flex-1 h-12 bg-[#4A7C59] hover:bg-[#3A6345] text-white"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Anteprima Email
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Bottone Richiedi Ricetta (da usare nella dashboard)
const RichiestaRicettaButton = ({ 
  isPremium, 
  medicoSettings, 
  pazienteNome,
  pazienteEta,
  famigliaNome,
  userEmail,
  userPhone,
  famigliaId,
  medicines = [],
  onUpgrade,
  onMedicoConfigured
}) => {
  const [showModal, setShowModal] = useState(false);

  if (!isPremium) {
    return (
      <button
        onClick={onUpgrade}
        className="relative w-full bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white rounded-2xl p-5 text-left overflow-hidden group hover:shadow-lg transition-all"
        data-testid="btn-richiedi-ricetta-locked"
      >
        <div className="absolute top-2 right-2">
          <Crown className="w-6 h-6 text-white/80" />
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <FileText className="w-7 h-7" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Richiedi Ricetta al Medico</h3>
            <p className="text-sm text-white/80">Funzione Premium - Passa a Premium per sbloccare</p>
          </div>
        </div>
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="w-full bg-gradient-to-r from-[#4A7C59] to-[#6B9B7A] text-white rounded-2xl p-5 text-left hover:shadow-lg transition-all group"
        data-testid="btn-richiedi-ricetta"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
            <FileText className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg">Richiedi Ricetta</h3>
            <p className="text-sm text-white/80">
              {medicoSettings?.nome_medico 
                ? `Invia richiesta al ${medicoSettings.nome_medico}`
                : 'Configura il medico e invia la richiesta'}
            </p>
          </div>
          <ChevronRight className="w-6 h-6 text-white/60" />
        </div>
      </button>

      <RichiestaRicettaModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        medicoSettings={medicoSettings}
        pazienteNome={pazienteNome}
        pazienteEta={pazienteEta}
        famigliaNome={famigliaNome}
        userEmail={userEmail}
        userPhone={userPhone}
        famigliaId={famigliaId}
        medicines={medicines}
        onMedicoConfigured={onMedicoConfigured}
      />
    </>
  );
};

export { RichiestaRicettaModal, RichiestaRicettaButton };
export default RichiestaRicettaButton;
